import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Menu, LayoutDashboard, UserCircle2, Search, Bookmark, Briefcase, Sparkles, Settings as SettingsIcon, LogOut, Moon, Sun, Building2, ShieldCheck, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Separator } from "@/components/ui/separator";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/jobs", label: "Job Search", icon: Search },
  { to: "/companies", label: "GCC Directory", icon: Building2 },
  { to: "/profile", label: "My Profile", icon: UserCircle2 },
  { to: "/saved", label: "Saved Jobs", icon: Bookmark },
  { to: "/applications", label: "Applications", icon: Briefcase },
];
const ADMIN_ONLY_NAV = [
  { to: "/content-studio", label: "Content Studio", icon: Sparkles },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const isActive = (to) => location.pathname.startsWith(to);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-40 glass-bar" data-testid="app-topbar">
        <div className="container flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="menu-toggle-btn" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 p-0 bg-background" data-testid="left-menu-sheet">
                <SheetHeader className="p-6 pb-4 border-b border-border">
                  <SheetTitle className="font-serif italic text-2xl tracking-tight">कर्म · KARM</SheetTitle>
                  <p className="eyebrow">Elite GCC Careers</p>
                </SheetHeader>

                <div className="px-3 py-4">
                  {user ? (
                    <div className="flex items-center gap-3 px-3 py-3 rounded-lg bg-muted mb-3" data-testid="menu-user-card">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.picture} />
                        <AvatarFallback>{(user.name || user.email).slice(0, 2).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{user.name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {user.role === "candidate" ? "Seeker" : user.role === "recruiter" ? "Recruiter" : user.role === "admin" ? "Admin" : user.email}
                        </div>
                      </div>
                      {user.is_subscribed && <Crown className="h-4 w-4 text-vital-blue ml-auto" />}
                    </div>
                  ) : (
                    <div className="px-3 pb-3">
                      <Button asChild className="w-full" data-testid="menu-signin-btn" onClick={() => setOpen(false)}>
                        <Link to="/auth">Sign in</Link>
                      </Button>
                    </div>
                  )}

                  <nav className="flex flex-col gap-1">
                    {NAV.map(({ to, label, icon: Icon }) => (
                      <Link
                        key={to}
                        to={to}
                        onClick={() => setOpen(false)}
                        data-testid={`nav-${to.slice(1)}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                          isActive(to) ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {label}
                      </Link>
                    ))}

                    {user?.role === "admin" && ADMIN_ONLY_NAV.map(({ to, label, icon: Icon }) => (
                      <Link
                        key={to}
                        to={to}
                        onClick={() => setOpen(false)}
                        data-testid={`nav-${to.slice(1)}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                          isActive(to) ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {label}
                      </Link>
                    ))}

                    {user?.role === "admin" ? (
                      <Link
                        to="/admin"
                        onClick={() => setOpen(false)}
                        data-testid="nav-admin"
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                          isActive("/admin") ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground"
                        }`}
                      >
                        <ShieldCheck className="h-4 w-4" />
                        Admin Console
                      </Link>
                    ) : user?.role === "recruiter" ? (
                      <Link
                        to="/admin"
                        onClick={() => setOpen(false)}
                        data-testid="nav-recruiter"
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                          isActive("/admin") ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground"
                        }`}
                      >
                        <ShieldCheck className="h-4 w-4" />
                        Recruiter Tools
                      </Link>
                    ) : null}
                  </nav>

                  <Separator className="my-4" />

                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => { toggle(); }}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold hover:bg-muted text-left"
                      data-testid="menu-theme-toggle-btn"
                    >
                      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                      {theme === "dark" ? "Light Mode" : "Night Mode"}
                    </button>
                    <Link
                      to="/settings"
                      onClick={() => setOpen(false)}
                      data-testid="nav-settings"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold hover:bg-muted"
                    >
                      <SettingsIcon className="h-4 w-4" />
                      Settings
                    </Link>
                    {user?.role === "admin" && (
                      <Link
                        to="/pricing"
                        onClick={() => setOpen(false)}
                        data-testid="nav-pricing"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold hover:bg-muted"
                      >
                        <Crown className="h-4 w-4" />
                        KARM Plus — $5/mo
                      </Link>
                    )}
                    {user && (
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold hover:bg-muted text-left"
                        data-testid="logout-btn"
                      >
                        <LogOut className="h-4 w-4" />
                        Sign out
                      </button>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
              <span className="font-devanagari text-2xl leading-none">कर्म</span>
              <span className="font-serif italic text-lg tracking-tight">KARM</span>
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm" data-testid="topbar-jobs-btn">
              <Link to="/jobs">Jobs</Link>
            </Button>
            <Button asChild variant="ghost" size="sm" data-testid="topbar-companies-btn" className="hidden md:inline-flex">
              <Link to="/companies">Companies</Link>
            </Button>
            <Button onClick={toggle} variant="ghost" size="icon" data-testid="theme-toggle-btn" aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            {user ? (
              (() => {
                const isStaff = user.role === "recruiter" || user.role === "admin";
                if (isStaff) {
                  return (
                    <Button asChild size="sm" data-testid="topbar-post-btn">
                      <Link to="/admin">Post a Job</Link>
                    </Button>
                  );
                }
                // Content Studio / KARM Plus are admin-only for now — no CTA for seekers.
                return null;
              })()
            ) : (
              <Button asChild size="sm" data-testid="topbar-signin-btn">
                <Link to="/auth">Sign in</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border mt-16 py-10">
        <div className="container flex flex-col md:flex-row md:items-center justify-between gap-4 text-sm text-muted-foreground">
          <div>
            <span className="font-serif italic text-foreground">KARM</span> · Where elite GCC careers blossom.
          </div>
          <div className="flex flex-wrap gap-5">
            {user?.role === "admin" && <Link to="/pricing" data-testid="footer-pricing">Pricing</Link>}
            <Link to="/companies" data-testid="footer-companies">Companies</Link>
            <Link to="/jobs" data-testid="footer-jobs">Jobs</Link>
            <Link to="/contact" data-testid="footer-contact">Contact Us</Link>
            <Link to="/privacy" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms">Terms &amp; Conditions</Link>
            {!user && <Link to="/auth" data-testid="footer-signin">Sign in</Link>}
          </div>
        </div>
      </footer>
    </div>
  );
}
